import React from "react";
import { Box, Container, Typography, Fade, Divider } from "@mui/material";
import { Helmet } from "react-helmet";

// 🖼️ Import Assets
import SoftService from "../assets/SoftService.png";
import PestManagment from "../assets/PestManagment.png";
import Lundary from "../assets/Lundary.png";
import Catering from "../assets/Catering.png";
import Civil from "../assets/Civil.png";
import MEP from "../assets/MEP.png";
import SpaceUtilization from "../assets/SpaceUtilizationStandard.png";

export default function ServicesSection() {
  const services = [
    {
      title: "Housekeeping Services",
      image: SoftService,
      desc: `Our housekeeping services redefine cleanliness with science and precision.
      We blend advanced mechanized systems, eco-safe materials, and trained staff to maintain
      world-class hygiene standards. Every surface reflects care, consistency, and a sense of order
      — turning spaces into refreshing environments where productivity and positivity thrive.`,
    },
    {
      title: "Landscaping & Space Utilization",
      image: SpaceUtilization,
      desc: `We design landscapes that breathe life into workplaces. From manicured lawns to ambient
      water features, our green solutions balance aesthetics with sustainability. Through intelligent
      space planning, we maximize efficiency and beauty — creating inspiring, nature-aligned workspaces.`,
    },
    {
      title: "Pest Management Services",
      image: PestManagment,
      desc: `Our pest management system focuses on prevention and protection — not just control.
      Using odorless, eco-certified chemicals and advanced monitoring, we safeguard cafeterias, offices,
      and critical facilities with precise, safe, and long-lasting solutions that ensure uninterrupted operations.`,
    },
    {
      title: "Mechanized Laundry Services",
      image: Lundary,
      desc: `Our fully mechanized laundry units serve institutions, hospitality clients, and
      healthcare environments with precision care. Automated cycles ensure hygiene integrity, stain removal,
      and material protection — giving every fabric a renewed, fresh finish, every single time.`,
    },
    {
      title: "Pantry, Cafeteria & Catering Management",
      image: Catering,
      desc: `We manage nourishment with heart and hygiene. From pantry staffing and meal planning to
      end-to-end cafeteria management, we curate menus that balance nutrition, taste, and freshness.
      Our catering teams elevate dining experiences that energize people and strengthen community bonds.`,
    },
    {
      title: "Civil Services",
      image: Civil,
      desc: `Our civil engineering teams deliver end-to-end project excellence. From renovation and
      refurbishment to new builds, we ensure safety, compliance, and durability. Every structure we handle
      stands as a statement of quality craftsmanship and intelligent design.`,
    },
    {
      title: "MEP Services",
      image: MEP,
      desc: `Our Mechanical, Electrical, and Plumbing (MEP) services ensure operational continuity.
      Through preventive maintenance, energy optimization, and automation, we create infrastructure
      that performs flawlessly — powering seamless functionality across all facility systems.`,
    },
  ];

  return (
    <>
      <Helmet>
        <title>Our Services - Namami India Support Services</title>
        <meta
          name="description"
          content="Explore Namami India's complete range of facility, hospitality, and engineering management services — tailored for reliability, efficiency, and excellence."
        />
        <meta
          name="keywords"
          content="Namami India, services, facility management, housekeeping, catering, maintenance, Odisha, MEP, pest control, landscaping"
        />
        <meta property="og:title" content="Our Services - Namami India" />
        <meta
          property="og:description"
          content="Namami India offers comprehensive facility and engineering management services focused on quality, safety, and innovation."
        />
        <meta
          property="og:image"
          content="https://www.namamiindia.co.in/assets/namami-logo.png"
        />
        <meta
          property="og:url"
          content="https://www.namamiindia.co.in/services"
        />
        <link rel="canonical" href="https://www.namamiindia.co.in/services" />
      </Helmet>

      {/* === Hero Section === */}
      <Box
        sx={{
          py: { xs: 10, md: 14 },
          background: "linear-gradient(180deg, #004ba0, #002d6b)",
          color: "white",
          textAlign: "center",
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h3"
            fontWeight={900}
            mb={3}
            sx={{ letterSpacing: "0.5px" }}
          >
            Integrated Services, One Trusted Partner
          </Typography>
          <Typography
            variant="body1"
            sx={{
              lineHeight: 1.9,
              fontSize: "1.1rem",
              opacity: 0.95,
            }}
          >
            At Namami India, we go beyond maintenance — we create environments
            that function flawlessly and feel inspiring. Our services blend
            modern technology with a human touch, ensuring every facility
            remains safe, efficient, and future-ready. With every service, we
            add measurable value to the organizations we serve.
          </Typography>
        </Container>
      </Box>

      {/* === Main Services Grid === */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          backgroundColor: "#fff",
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h4"
            fontWeight={800}
            textAlign="center"
            mb={2}
            sx={{
              background: "linear-gradient(90deg, #1565c0, #004ba0)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Our Comprehensive Services
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            textAlign="center"
            maxWidth="720px"
            mx="auto"
            mb={8}
            sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}
          >
            Every service we deliver carries a purpose — to enhance comfort,
            ensure safety, and build operational excellence. From hospitality to
            engineering, our approach combines innovation and accountability.
          </Typography>

          {services.map((service, index) => (
            <Fade in timeout={700 + index * 150} key={index}>
              <Box
                sx={{
                  backgroundColor: "white",
                  borderRadius: 4,
                  textAlign: "center",
                  p: { xs: 3, md: 5 },
                  mb: 8,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-6px)",
                  },
                }}
              >
                <Box
                  component="img"
                  src={service.image}
                  alt={service.title}
                  sx={{
                    width: { xs: "85%", md: "65%" },
                    maxWidth: 550,
                    mb: 4,
                    borderRadius: 0,
                    boxShadow: "none",
                    objectFit: "contain",
                  }}
                />
                <Typography
                  variant="h5"
                  fontWeight={700}
                  mb={2}
                  sx={{
                    color: "#0d47a1",
                    letterSpacing: "0.5px",
                  }}
                >
                  {service.title}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    lineHeight: 1.9,
                    fontSize: "1.06rem",
                    textAlign: "justify",
                    maxWidth: "850px",
                    mx: "auto",
                  }}
                >
                  {service.desc}
                </Typography>
              </Box>
            </Fade>
          ))}
        </Container>
      </Box>

      {/* === Inverse Hero Philosophy Section === */}
      <Box
        sx={{
          py: { xs: 10, md: 14 },
          background: "linear-gradient(180deg, #f5f9ff, #e3f2fd)",
          textAlign: "center",
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h4"
            fontWeight={800}
            mb={3}
            sx={{
              background: "linear-gradient(90deg, #1565c0, #004ba0)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Our Philosophy of Service
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              lineHeight: 1.9,
              fontSize: "1.08rem",
              maxWidth: "800px",
              mx: "auto",
            }}
          >
            Service, to us, is more than a contract — it’s a commitment to
            transform every client relationship into a partnership built on
            trust, accountability, and measurable outcomes. Every project we
            manage reflects our core values: <strong>Quality, Safety, and
            Consistency</strong>. Our mission is simple — to make every space
            smarter, cleaner, and more human.
          </Typography>
        </Container>
      </Box>

      {/* === Closing Tagline === */}
      <Box sx={{ py: 8, textAlign: "center", backgroundColor: "#fff" }}>
        <Divider
          sx={{
            my: 6,
            width: "60%",
            mx: "auto",
            backgroundColor: "rgba(21, 101, 192, 0.3)",
          }}
        />
        <Typography
          variant="h6"
          fontWeight={700}
          color="primary"
          sx={{ letterSpacing: "1px" }}
        >
          Our Focus: Quality • Safety • Efficiency • Cost Control
        </Typography>
      </Box>
    </>
  );
}
